import { Box } from "@mui/material";
import { ReactElement } from "react";
import { IoPerson } from "react-icons/io5";
import { LuPencilOff } from "react-icons/lu";
import { MdFreeCancellation } from "react-icons/md";
import colors from "../../colors";
import { Calendar } from "../../store";
import { CalendarAccessIndicatorType, getCalendarAccessIndicatorTypes } from "../../utils/scheduleUtils";
import { CabIcon, CabTooltip } from "../CabComponents";

const calendarAccessIndicatorConfig = {
  readOnly: {
    Icon: LuPencilOff,
    tooltip: "You do not have permission to add or remove events for this calendar.",
  },
  freeBusy: {
    Icon: MdFreeCancellation,
    tooltip: "You only have Free/Busy access to this calendar.",
  },
  delegate: {
    Icon: IoPerson,
    tooltip: "You have delegate access to this calendar.",
  },
};

const calendarAccessIndicatorOrder: CalendarAccessIndicatorType[] = ['delegate', 'readOnly', 'freeBusy'];

type CalendarAccessIndicatorsProps = {
  calendar: Pick<Calendar, 'canEdit' | 'freeBusy' | 'readable' | 'delegatorUser'>;
  reserveColumns?: boolean;
};

export const CalendarAccessIndicators = ({
  calendar,
  reserveColumns = false,
}: CalendarAccessIndicatorsProps): ReactElement | null => {
  const indicators = getCalendarAccessIndicatorTypes(calendar);
  if (!indicators.length) return null;
  const indicatorSet = new Set(indicators);
  const reservedColumnIndicators: (CalendarAccessIndicatorType | null)[] = [
    indicatorSet.has('delegate') && indicatorSet.has('freeBusy') ? 'freeBusy' : null,
    indicatorSet.has('delegate') ? 'delegate' : indicatorSet.has('freeBusy') ? 'freeBusy' : null,
    indicatorSet.has('readOnly') ? 'readOnly' : null,
  ];
  const orderedIndicators = reserveColumns ? reservedColumnIndicators : calendarAccessIndicatorOrder;

  return (
    <Box
      component="span"
      sx={{
        alignItems: "center",
        columnGap: 0.5,
        display: reserveColumns ? "inline-grid" : "inline-flex",
        gridTemplateColumns: reserveColumns ? "repeat(3, 18px)" : undefined,
        justifyItems: "center",
      }}
    >
      {orderedIndicators.map((indicator, idx) => {
        if (indicator == null || !indicatorSet.has(indicator)) {
          return reserveColumns ? <Box key={`empty-${idx}`} component="span" sx={{ height: 18, width: 18 }} /> : null;
        }
        const config = calendarAccessIndicatorConfig[indicator];
        return (
          <CabTooltip key={`${indicator}-${idx}`} title={config.tooltip} wrapWithSpan>
            <CabIcon
              Icon={config.Icon}
              color={colors.black600}
              size="small"
            />
          </CabTooltip>
        );
      })}
    </Box>
  );
};
